import { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Text,
  Title,
  Group,
  Badge,
  Stack,
  RingProgress,
  Image,
  Box,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import { useAuth } from '../hooks/useAuth';
import { getUserBetSlips } from '../services/api';
import { BetSlipWithSelections, BetSlipStatus, mapBetSlipData } from '../types/betslip';
import betfolioLogo from '../assets/betfolio-logo-gfx-orig.png';
import { BetSlipDetails } from '../components/BetSlipDetails';

const statusColors: Record<BetSlipStatus, string> = {
  pending: 'yellow',
  won: 'green',
  lost: 'red',
  push: 'gray',
  cancelled: 'gray',
};

export default function Dashboard() {
  const { user } = useAuth();
  const [betSlips, setBetSlips] = useState<Partial<BetSlipWithSelections>[]>([]);
  const [selectedBetSlip, setSelectedBetSlip] = useState<Partial<BetSlipWithSelections> | null>(null);

  useEffect(() => {
    const fetchBetSlips = async () => {
      if (user?.id) {
        const data = await getUserBetSlips(user.id);
        const mappedData = mapBetSlipData(data);
        setBetSlips(mappedData);
      }
    };

    fetchBetSlips();
  }, [user]);

  const calculateStats = () => {
    const total = betSlips.length;
    const won = betSlips.filter((slip) => slip.status === 'won').length;
    const lost = betSlips.filter((slip) => slip.status === 'lost').length;
    const pending = betSlips.filter((slip) => slip.status === 'pending').length;
    const push = betSlips.filter((slip) => slip.status === 'push').length;
    const cancelled = betSlips.filter((slip) => slip.status === 'cancelled').length;

    const totalWagered = betSlips.reduce((sum, slip) => sum + (slip.wagerAmount || 0), 0);
    const totalPayout = betSlips
      .filter((slip) => slip.status === 'won')
      .reduce((sum, slip) => sum + (slip.potentialPayout || 0), 0);

    const winRate = total > 0 ? (won / (won + lost)) * 100 : 0;

    return {
      total,
      won,
      lost,
      pending,
      push,
      cancelled,
      totalWagered,
      totalPayout,
      winRate,
    };
  };

  const stats = calculateStats();

  return (
    <Container size="xl">
      {/* Welcome Section */}
      <Paper shadow="xs" p="xl" mb="xl" withBorder>
        <Group position="apart" align="flex-start">
          <Box>
            <Title order={1} mb="md">Welcome to Betfolio</Title>
            <Text size="xl" color="dimmed" mb="lg" style={{ maxWidth: '600px' }}>
              Professionalize your sports betting by building your betting portfolio with Betfolio
            </Text>
            <Text mb="md">
              Betfolio helps you track and analyze your sports betting activity with powerful tools:
            </Text>
            <Grid mb="md">
              <Grid.Col span={6}>
                <Stack spacing="xs">
                  <Text>• Upload bet slips via image or camera</Text>
                  <Text>• Track wins, losses, and payouts</Text>
                  <Text>• Monitor your betting performance</Text>
                </Stack>
              </Grid.Col>
              <Grid.Col span={6}>
                <Stack spacing="xs">
                  <Text>• Maintain digital records</Text>
                  <Text>• View comprehensive statistics</Text>
                  <Text>• Analyze betting patterns</Text>
                </Stack>
              </Grid.Col>
            </Grid>
          </Box>
          <Image
            src={betfolioLogo}
            alt="Betfolio Logo"
            width={200}
            fit="contain"
          />
        </Group>
      </Paper>

      <Title order={2} mb="xl">
        Performance Overview
      </Title>

      <Grid>
        <Grid.Col span={4}>
          <Paper shadow="xs" p="md" withBorder>
            <Stack>
              <Title order={3}>Win Rate</Title>
              <Group position="center">
                <RingProgress
                  size={120}
                  thickness={12}
                  sections={[{ value: stats.winRate, color: 'blue' }]}
                  label={
                    <Text size="xs" align="center">
                      {stats.winRate.toFixed(1)}%
                    </Text>
                  }
                />
              </Group>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={4}>
          <Paper shadow="xs" p="md" withBorder>
            <Stack>
              <Title order={3}>Total Wagered</Title>
              <Text size="xl" weight={700} align="center">
                ${stats.totalWagered.toFixed(2)}
              </Text>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={4}>
          <Paper shadow="xs" p="md" withBorder>
            <Stack>
              <Title order={3}>Total Payout</Title>
              <Text size="xl" weight={700} align="center" color="green">
                ${stats.totalPayout.toFixed(2)}
              </Text>
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper shadow="xs" p="md" withBorder>
            <Title order={3} mb="md">
              Bet Status Overview
            </Title>
            <Group spacing="xl">
              {Object.entries(statusColors).map(([status]) => (
                <Group key={status} spacing="xs">
                  <Badge color={statusColors[status as BetSlipStatus]}>
                    {status.toUpperCase()}
                  </Badge>
                  <Text>
                    {betSlips.filter((slip) => slip.status === status).length}
                  </Text>
                </Group>
              ))}
            </Group>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper shadow="xs" p="md" withBorder>
            <Title order={3} mb="md">
              Recent Bets
            </Title>
            <Stack spacing="md">
              {betSlips.slice(0, 5).map((slip, index) => (
                <Paper key={index} shadow="xs" p="md" withBorder>
                  <Group position="apart">
                    <div>
                      <Group spacing="xs">
                        <Text weight={500}>Ticket ID: {slip.ticketId}</Text>
                        {slip.status && (
                          <Badge color={statusColors[slip.status as BetSlipStatus]}>
                            {slip.status.toUpperCase()}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" color="dimmed">
                        {slip.casinoName}
                      </Text>
                      <Text size="sm">
                        Wager: ${slip.wagerAmount?.toFixed(2)} | Potential Payout: $
                        {slip.potentialPayout?.toFixed(2)}
                      </Text>
                    </div>
                    <Group>
                      <Tooltip label="View details">
                        <ActionIcon
                          variant="light"
                          color="blue"
                          onClick={() => setSelectedBetSlip(slip)}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Text size="sm" color="dimmed" ml="md">
                        {slip.eventDateTime
                          ? new Date(slip.eventDateTime).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            })
                          : 'Date not available'}
                      </Text>
                    </Group>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid.Col>
      </Grid>

      {selectedBetSlip && (
        <BetSlipDetails
          betSlip={selectedBetSlip}
          opened={!!selectedBetSlip}
          onClose={() => setSelectedBetSlip(null)}
          onUpdate={async () => {
            if (user?.id) {
              const data = await getUserBetSlips(user.id);
              const mappedData = mapBetSlipData(data);
              setBetSlips(mappedData);
            }
          }}
        />
      )}
    </Container>
  );
}